import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import {
  togglePanel,
  closePanel,
  checkHealth,
  checkBotAccess,
  fetchHistory,
} from "../chatBotSlice";
import ChatBubble from "./ChatBubble";
import ChatPanel from "./ChatPanel";

// ═══════════════════════════════════════════════════════════════
//  Animation Keyframes (injected once via useEffect)
// ═══════════════════════════════════════════════════════════════

const KEYFRAMES = `
@keyframes midasBubbleEntrance {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes midasPulse {
  0%, 100% { box-shadow: 0 4px 16px rgba(212,160,23,0.3), 0 2px 6px rgba(0,0,0,0.1); }
  50% { box-shadow: 0 4px 16px rgba(212,160,23,0.3), 0 2px 6px rgba(0,0,0,0.1), 0 0 0 8px rgba(212,160,23,0.15); }
}
@keyframes midasUnreadPop {
  0% { transform: scale(0); }
  60% { transform: scale(1.3); }
  100% { transform: scale(1); }
}
@keyframes midasPanelOpen {
  0% { opacity: 0; transform: translateY(16px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes midasPanelClose {
  0% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(8px) scale(0.97); }
}
@keyframes midasPanelSlideUp {
  0% { transform: translateY(100vh); }
  100% { transform: translateY(0); }
}
@keyframes midasPanelSlideDown {
  0% { transform: translateY(0); }
  100% { transform: translateY(100vh); }
}
@keyframes midasBotMsg {
  0% { opacity: 0; transform: translateX(-12px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes midasUserMsg {
  0% { opacity: 0; transform: translateX(12px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes midasChipStagger {
  0% { opacity: 0; transform: translateX(-8px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes midasDotBounce {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1); }
}
@keyframes midasWelcomeAvatar {
  0% { opacity: 0; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes midasFadeUp {
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}
`;

// ═══════════════════════════════════════════════════════════════
//  ChatWidget -- top-level wrapper
// ═══════════════════════════════════════════════════════════════

const ChatWidget: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isOpen = useAppSelector((state) => state.chatBot.isOpen);
  const hasUnread = useAppSelector((state) => state.chatBot.hasUnread);
  const isBotAvailable = useAppSelector((state) => state.chatBot.isBotAvailable);
  const isBotEnabled = useAppSelector((state) => state.chatBot.isBotEnabled);
  const user = useAppSelector((state) => state.account.user);

  // Inject keyframes
  useEffect(() => {
    const styleId = "midas-chat-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  // Check bot access and health on mount
  useEffect(() => {
    if (user) {
      dispatch(checkBotAccess());
      dispatch(checkHealth());
    }
  }, [dispatch, user]);

  // Periodic health check every 60 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      dispatch(checkHealth());
    }, 60000);
    return () => clearInterval(interval);
  }, [dispatch, user]);

  // Load history when panel opens for the first time
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchHistory());
    }
  }, [isOpen, dispatch]);

  // Hide chat widget entirely when on the MIDAS full-screen page
  const isOnMidasPage = location.pathname === "/midas";

  // BUG-MIDAS-MOBILE-0414-B: Hide the popover/bubble entirely on phone.
  // On phones the floating MIDAS popover is redundant with the full-screen
  // /midas page (and eats precious screen space). Mobile users should only
  // access MIDAS via the dedicated /midas route. Desktop + tablet keep the
  // existing floating bubble behaviour.
  const isPhone = useMediaQuery("(max-width:768px)");

  // Visibility check: user must be logged in and bot must be enabled.
  // isBotAvailable (health check) is NOT required for visibility --
  // we show the bubble and handle bot-down gracefully inside the panel.
  // This avoids hiding the entire widget when the bot has a brief outage.
  const isVisible =
    Boolean(user) && isBotEnabled && !isOnMidasPage && !isPhone;

  if (!isVisible) return null;

  const handleToggle = () => {
    dispatch(togglePanel());
  };

  const handleClose = () => {
    dispatch(closePanel());
  };

  return (
    <>
      <ChatPanel isOpen={isOpen} onClose={handleClose} />
      <ChatBubble
        isPanelOpen={isOpen}
        hasUnread={hasUnread}
        onClick={handleToggle}
      />
    </>
  );
};

export default ChatWidget;
